import { Res } from 'core/api';
import * as SalesStore from 'pos/modules/main/SalesStore';
import { PaymentMethodDTO } from 'core/api';
import { SessionProvider } from 'core/infrastructure';
import { CashdrawerFacade } from 'pos/facades';
import { processPayment, getAdvancedPaymentData } from 'pos/modules/helpers/payments-processing';
import { closeCurrentTicketFromDirectPayment } from './doDirectPaymentCommand';
import { blockTicketWhileClosing } from 'pos/modules/helpers/document-creation';
import { PrepaymentStatus } from 'pos/modules/helpers/payments-processing';
import { Message } from 'core/ui-next/modal';

type ShowScreenParams = { pendingAmount: number, introducedAmount: Nullable<number>, method: PaymentMethodDTO };
export function shouldShowScreen({ pendingAmount, introducedAmount, method }: ShowScreenParams): { showScreen: boolean, overpay: Nullable<'Tip' | 'Change'> } {
	// Si no hay nada pendiente de pago, da igual que metan o no cantidad, que cerramos la factura
	if (pendingAmount === 0)
		return { showScreen: false, overpay: null };

	if (introducedAmount === null || introducedAmount === 0)
		return { showScreen: true, overpay: null };

	if (introducedAmount < pendingAmount)
		return { showScreen: false, overpay: null };

	if (introducedAmount === pendingAmount)
		return { showScreen: false, overpay: null };

	if (method.GiveChange && method.RegisterTip)
		return { showScreen: true, overpay: null };

	if (!method.GiveChange && !method.RegisterTip)
		return { showScreen: !method.AllowOverPaid, overpay: null };

	return { showScreen: false, overpay: method.RegisterTip ? 'Tip' : 'Change' };
}

export async function doAdvancedDirectPayment({ methodId, introducedAmount, imageSrc }: { methodId: number, introducedAmount: Nullable<number>, imageSrc: string }) {

	return blockTicketWhileClosing(SalesStore.getTicket().Id, async () => {

		if (!SessionProvider.getCurrent().AllowCashTransactions) {
			await Message.show(Res.Msg.MSG_CURRENT_POS_DOES_NOT_ALLOW_CASH_TRANSACTIONS());
			return;
		}

		const isRefund = SalesStore.ticketCanBeClosedAsRefund();
		if (isRefund) {
			await Message.show(Res.Msg.MSG_CANNOT_PROCESS_ADVANCED_PAYMENT_ON_REFUND());
			return;
		}

		const operation = await CashdrawerFacade.createPaymentOperation();
		const method = operation.Methods.find(x => x.Id === methodId);
		if (!method) {
			await Message.show(Res.Msg.MSG_CANNOT_EMIT_PAYMENT_WITH_SELECTED_METHOD());
			return;
		}

		const pendingAmount = SalesStore.getTicket().PendingAmount;
		if (pendingAmount < 0) {
			await Message.show(Res.Msg.MSG_TO_CLOSE_NEGATIVE_DOCUMENT_REFUND_IS_NEEDED());
			return;
		}

		let paidAmount = introducedAmount ?? pendingAmount;
		let tipAmount = 0;

		if (paidAmount < 0) {
			await Message.show(Res.Msg.MSG_CANNOT_PROCESS_ADVANCED_PAYMENT_ON_NEGATIVE_INTRODUCED_AMOUNT());
			return;
		}

		const { showScreen, overpay } = shouldShowScreen({ pendingAmount, introducedAmount, method });

		if (showScreen) {

			const result = await getAdvancedPaymentData(pendingAmount, paidAmount, method, operation.MaxAvailableChange, imageSrc);
			if (!result.ok)
				return;

			paidAmount = result.value.PaidAmount;
			tipAmount = result.value.TipAmount;
		}

		if (overpay === 'Tip') {
			const overpayAmount = paidAmount - pendingAmount;
			tipAmount = overpayAmount;
			paidAmount = pendingAmount;
		}

		if (paidAmount >= pendingAmount) {

			const canPay = await PrepaymentStatus.canPay({ Method: method, AmountToPay: pendingAmount, PaidAmount: paidAmount, TipAmount: tipAmount });
			if (!canPay)
				return;

			await closeCurrentTicketFromDirectPayment({
				closeAs: 'Invoice',
				method,
				getAmountToPay: () => paidAmount,
				getTipAmount: () => tipAmount,
				getPendingAmount: () => pendingAmount
			});
		}
		else
			// Añadir pagos
			await processPayment({
				mode: 'ADD',
				amountToPay: paidAmount,
				method,
				paidAmount,
				tipAmount,
				process: (payments, prepayAmount) => SalesStore.addPayments(payments, prepayAmount),
				ticketGlobalId: SalesStore.getTicket().GlobalId
			});
	});
}

